import { spacing } from "@indeed/ifl-tokens/react-native";
import React from "react";
import { StyleSheet } from "react-native";

import { Alert, Box, Button, RadioSelectorList, Text, TextArea } from "../../../mads";
import { useI18n } from "../../I18n";
import { REPORT_JOB_FEEDBACK_MAX_LENGTH } from "../../util/reportContent/ContentProblem";

export { ReportJobFormLayout, type ReportJobFormLayoutProps } from "./ReportJobFormLayout";
export { useReportJobFormState } from "./useReportJobFormState";
export { REPORT_JOB_FEEDBACK_MAX_LENGTH };

export interface ReportJobOption<TValue extends string = string> {
  value: TValue;
  label: string;
  testID?: string;
}

export interface ReportJobFormProps<TValue extends string = string> {
  jobTitle: string;
  companyName?: string;
  reportOptions: ReadonlyArray<ReportJobOption<TValue>>;
  selectedReason?: TValue;
  moreInfo: string;
  canSubmit: boolean;
  feedbackMaxLength?: number;
  /**
   * When true (jcs_report_job_tst), uses the updated details-field header/subheader copy.
   */
  useUpdatedDetailsPrompt?: boolean;
  onSelectReason: (reason: TValue) => void;
  onMoreInfoChange: (text: string) => void;
  onSubmit: () => void;
}

/**
 * Shared report-job form UI. Submission and feature-specific state are supplied by each surface.
 */
export function ReportJobForm<TValue extends string>({
  jobTitle,
  companyName,
  reportOptions,
  selectedReason,
  moreInfo,
  canSubmit,
  feedbackMaxLength = REPORT_JOB_FEEDBACK_MAX_LENGTH,
  useUpdatedDetailsPrompt = false,
  onSelectReason,
  onMoreInfoChange,
  onSubmit,
}: ReportJobFormProps<TValue>) {
  const i18n = useI18n();
  const selectedIndex = reportOptions.findIndex((option) => option.value === selectedReason);
  const exceedsCharacterLimit = moreInfo.length > feedbackMaxLength;
  const moreInfoLabel = useUpdatedDetailsPrompt
    ? i18n.pgettext("report job details header", "What happened?")
    : i18n.pgettext("report job additional info box", "Additional information");
  const moreInfoExplainerText = useUpdatedDetailsPrompt
    ? i18n.pgettext("report job details subheader", "Optional: Add details about the issue.")
    : undefined;

  return (
    <>
      <RadioSelectorList
        labelText={jobTitle}
        explainerText={companyName}
        radioSelectorProps={reportOptions.map((option) => ({
          labelText: option.label,
          onSelect: () => {},
          testID: option.testID ?? `report-job-reason-${option.value}`,
        }))}
        selectedIndex={selectedIndex >= 0 ? selectedIndex : undefined}
        onSelect={(index) => {
          const selectedOption = reportOptions[index];
          if (selectedOption) {
            onSelectReason(selectedOption.value);
          }
        }}
      />
      <TextArea
        label={moreInfoLabel}
        explainerText={moreInfoExplainerText}
        onInput={onMoreInfoChange}
        counterLimit={feedbackMaxLength}
        value={moreInfo}
        textAreaHeight={80}
        invalid={exceedsCharacterLimit}
        invalidText={i18n.gettext("Invalid")}
        errorText={
          exceedsCharacterLimit
            ? i18n.gettext("Please shorten your response to {0} characters.", [
                String(feedbackMaxLength),
              ])
            : undefined
        }
      />
      <Box sx={styles.privacyAlertContainer}>
        <Alert
          status="info"
          title={i18n.gettext("Protect your privacy")}
          explainerText={i18n.gettext(
            "Avoid disclosing personal information like your name, phone number, or any details that may personally identify you.",
          )}
        />
      </Box>
      <Button
        size="lg"
        onPress={onSubmit}
        disabled={!canSubmit || exceedsCharacterLimit}
        testID="report-job-submit-button"
      >
        {i18n.gettext("Submit")}
      </Button>
      <Text level={1} sx={styles.legalDisclaimerText}>
        {i18n.pgettext(
          "report job legal disclaimer",
          "All Job Ads are subject to Indeed's Terms of Service. We allow users to flag postings that may be in violation of those terms. Job Ads may also be flagged by Indeed. However, no moderation system is perfect, and flagging a posting does not ensure that it will be removed.",
        )}
      </Text>
    </>
  );
}

const styles = StyleSheet.create({
  privacyAlertContainer: {
    marginVertical: spacing.s3,
  },
  legalDisclaimerText: {
    marginTop: spacing.s1,
    flexGrow: 1,
  },
});
